<script lang="ts" setup>
import {
  tpgIconAdd,
  tpgIconCollectionsBookmark,
  tpgIconLogout,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarMenuItemProps,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarPrimaryButton,
  GrammarQuaternaryButton,
} from '@nora-monorepo/grammar-ui';
import { NORA_DISPLAY_VERSION } from '@nora-monorepo/shared';
import {
  AvatarImage,
  bgPrimary,
  borderSeparatorTransparency,
  getInitials,

  useAuthStore,
  useConfigStore,
} from '@nora-monorepo/ui-shared';
import type { GroupWithLocalRole, UserWithLocalRole } from '@nora-monorepo/ui-shared';

import { useFilesModalStore } from '#nora-chat-ui/components/modals/files-modal/files-modal-store';
import { ConversationModalActionState } from '#nora-chat-ui/components/modals/new-conversation/conversation-modal-types';
import ConversationModal from '#nora-chat-ui/components/modals/new-conversation/ConversationModal.vue';
import ThemeModal from '#nora-chat-ui/components/modals/theme-modal/ThemeModal.vue';
import ConversationListHeaderUserMenu from '#nora-chat-ui/components/sidebar/conversation-list/ConversationListHeaderUserMenu.vue';
import GlobalMute from '#nora-chat-ui/components/sidebar/conversation-list/global-mute/GlobalMute.vue';
import ChatSearchInput from '#nora-chat-ui/components/sidebar/search/ChatSearchInput.vue';
import { useAppStore } from '#nora-chat-ui/stores/app-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

const authStore = useAuthStore();
const userStore = useChatUserStore();
const filesModalStore = useFilesModalStore();
const { t } = useI18n();
const configStore = useConfigStore();
const appStore = useAppStore();
const { isStandaloneChat } = storeToRefs(appStore);
const showCreateConversationModal = ref(false);
const initialUsers = ref<UserWithLocalRole['user'][]>([]);
const initialGroups = ref<GroupWithLocalRole['group'][]>([]);

const menuOpen = ref(false);
const themeModalOpen = ref(false);
const userMenu = useTemplateRef<HTMLElement>('userMenu');

function getUserMenuItems(): GrammarMenuItemProps[] {
  const items: GrammarMenuItemProps[] = [
    {
      label: NORA_DISPLAY_VERSION,
      disabled: true,
    },
  ];

  if (isStandaloneChat.value) {
    items.push({
      label: 'Theme',
      onSelect: () => {
        themeModalOpen.value = true;
      },
    });
  }

  const showLogoutButton = configStore.appConfig?.general.showLogoutButton;

  if (showLogoutButton) {
    items.push({
      label: t('common.logout'),
      leadingIconTemplate: tpgIconLogout,
      onSelect: () => {
        authStore.logout();
      },
    });
  }

  return items;
}

const menuItems = ref<GrammarMenuItemProps[]>(getUserMenuItems());

function toggleNewConversationModal() {
  initialUsers.value = [];
  initialGroups.value = [];
  showCreateConversationModal.value = true;
}

function closeCreateConversationModal() {
  showCreateConversationModal.value = false;
  initialUsers.value = [];
  initialGroups.value = [];
}

async function toggleMenu(open?: boolean) {
  menuOpen.value = open ?? !menuOpen.value;
}
</script>

<template>
  <ConversationModal
    :is-open="showCreateConversationModal"
    :mode="ConversationModalActionState.Create"
    :initial-users="initialUsers"
    :initial-groups="initialGroups"
    @close="closeCreateConversationModal"
  />
  <div
    class="z-20 min-h-[48px] shrink-0 flex justify-between center-items border-b"
    :class="[bgPrimary, borderSeparatorTransparency]"
  >
    <div class="flex items-center font-semibold min-w-0 w-full">
      <div ref="userMenu" class="relative mr-1 ml-3">
        <ConversationListHeaderUserMenu
          v-if="menuOpen"
          :menu-items="menuItems"
          :ignored-click-outside-elements="[userMenu]"
          @close="toggleMenu"
        />
        <button class="p-0! !focus:border-none" @click="() => toggleMenu()">
          <AvatarImage
            v-if="userStore.user"
            :id="userStore.user.id"
            :text="getInitials(userStore.user)"
            :is-small="true"
            class="m-1"
          />
        </button>
      </div>

      <div class="relative mx-1">
        <GlobalMute />
      </div>

      <div class="ml-auto pr-2">
        <GrammarQuaternaryButton
          :icon-template="tpgIconCollectionsBookmark"
          @click="() => filesModalStore.openModal()"
        />
      </div>
    </div>
  </div>
  <div class="flex py-2 mx-2 border-b" :class="borderSeparatorTransparency">
    <ChatSearchInput />
    <GrammarPrimaryButton
      :icon-template="tpgIconAdd"
      class="ml-2"
      @click="toggleNewConversationModal"
    />

    <ThemeModal :is-open="themeModalOpen" @close="themeModalOpen = false" />
  </div>
</template>
